import { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { useAuth } from './AuthContext';
import { API_BASE } from './apiConfig';
import { useLinkedPlayers } from './api/useLinkedPlayers';
import { useLiveList } from './SocketContext';
import { Banner, Breadcrumb, Button, DataTable, DotLabel, EmptyState, ErrorState, Figures, Marking, Skel, Tabs } from './components/ui';
import UserMetricsChart from './UserMetricsChart';
import GameReviewsChart from './GameReviewsChart';
import RangeOfMotionChart from './RangeOfMotionChart';
import GameParameters from './GameParameters';
import {
    failMessage, fmtDateTime, fmtDay, fmtDuration, gameLabel, readError, readJson, relativeDay, sentence, sessionLength,
} from './components/patient/patientData';
import { GameCell, startedByLabel } from './sessionAccess';
import './PatientPage.css';

/**
 * Patient detail — /players/:id (provider only).
 * Profile: the linked person from GET /api/users/{me}/linked (useLinkedPlayers).
 * Sessions: GET /api/sessions?user_id={id} (204 = none). Reviews: GET /api/ratings/historical?user_id={id}.
 * Range of motion: results.jointsData of this patient's sessions. Live: useLiveList() (socket).
 */

const TABS = [
    { value: 'overview', label: 'Overview' },
    { value: 'params', label: 'Game parameters' },
];
const RECENT = 6;
const STATUSES = new Set(['complete', 'pending', 'incomplete']);

/* ── Data ─────────────────────────────────────────────────────────────── */
function useSessions(id, enabled) {
    const { authFetch } = useAuth();
    const [state, setState] = useState({ loading: true, error: null, list: [] });
    const [nonce, setNonce] = useState(0);
    useEffect(() => {
        if (!id || !enabled) return undefined;
        let alive = true;
        setState((s) => ({ ...s, loading: true, error: null }));
        (async () => {
            try {
                const res = await authFetch(`${API_BASE}/api/sessions?user_id=${encodeURIComponent(id)}`);
                if (!res.ok) throw new Error(await readError(res));
                const data = await readJson(res); // 204 → null
                const list = (Array.isArray(data) ? data : []).filter((s) => s && s._id);
                list.sort((a, b) => (Number(b.startTime) || 0) - (Number(a.startTime) || 0));
                if (alive) setState({ loading: false, error: null, list });
            } catch (e) {
                if (alive) setState({ loading: false, error: failMessage(e), list: [] });
            }
        })();
        return () => { alive = false; };
    }, [authFetch, id, enabled, nonce]);
    return { ...state, retry: useCallback(() => setNonce((n) => n + 1), []) };
}

function useReviews(id, enabled) {
    const { authFetch } = useAuth();
    const [state, setState] = useState({ loading: true, error: null, list: [] });
    const [nonce, setNonce] = useState(0);
    useEffect(() => {
        if (!id || !enabled) return undefined;
        let alive = true;
        setState((s) => ({ ...s, loading: true, error: null }));
        (async () => {
            try {
                const res = await authFetch(`${API_BASE}/api/ratings/historical?user_id=${encodeURIComponent(id)}`);
                if (res.status === 404) {
                    if (alive) setState({ loading: false, error: null, list: [] });
                    return;
                }
                if (!res.ok) throw new Error(await readError(res));
                const data = await readJson(res);
                const list = (Array.isArray(data) ? data : []).map((r) => ({
                    name: r.Name,
                    lastPlayDate: r.LastPlayDate,
                    timeStamp: r.timestamp,
                    happiness: r.Happiness,
                    excitement: r.Excitement,
                    control: r.Control,
                    pain: r.Pain,
                }));
                if (alive) setState({ loading: false, error: null, list });
            } catch (e) {
                if (alive) setState({ loading: false, error: failMessage(e), list: [] });
            }
        })();
        return () => { alive = false; };
    }, [authFetch, id, enabled, nonce]);
    return { ...state, retry: useCallback(() => setNonce((n) => n + 1), []) };
}

/* Joint angle ranges from every session that has them (results / jointsData may be missing). */
function jointsFrom(sessions) {
    const out = [];
    sessions.forEach((s) => {
        const joints = s?.results?.jointsData;
        if (!Array.isArray(joints)) return;
        joints.forEach((j) => {
            const angleMin = Number(j?.angleMin);
            const angleMax = Number(j?.angleMax);
            const name = j?._name || j?.name;
            if (!name || !Number.isFinite(angleMin) || !Number.isFinite(angleMax)) return;
            out.push({
                sessionId: s._id,
                game: s.game,
                timestamp: s.startTime,
                startTime: s.startTime,
                endTime: s.endTime,
                name,
                angleMin,
                angleMax,
            });
        });
    });
    return out;
}

/* ── Pieces ───────────────────────────────────────────────────────────── */
function Section({ id, title, note, right, card, wide, children }) {
    return (
        <section aria-labelledby={id} className={`pd-sec${card ? ' pd-card' : ''}${wide ? ' pd-wide' : ''}`}>
            <div className="pd-sec__head">
                <h2 id={id} className="pd-sec__title">{title}</h2>
                {note ? <span className="pd-sec__note">{note}</span> : null}
                {right}
            </div>
            {children}
        </section>
    );
}

const NoteBox = ({ children }) => <div className="pd-notebox">{children}</div>;

function InlineError({ title, message, onRetry }) {
    return (
        <div role="alert" className="pd-inline-error">
            <span className="pd-inline-error__title">{title}</span>
            <span className="pd-inline-error__body">{sentence(message)}</span>
            <Button variant="secondary" onClick={onRetry}>Retry</Button>
        </div>
    );
}

function SkeletonGrid() {
    return (
        <div aria-busy="true" className="pd-grid">
            <span className="eg-sr-only">Loading patient history…</span>
            {[0, 1, 2, 3].map((i) => <Skel key={i} h={360} raised />)}
        </div>
    );
}

/* ── Overview tab ─────────────────────────────────────────────────────── */
function Overview({ patient, first, sessions, reviews }) {
    const live = useLiveList();
    const navigate = useNavigate();
    const { user } = useAuth();
    const { nameOf } = useLinkedPlayers();
    const [showAll, setShowAll] = useState(false);
    const joints = useMemo(() => jointsFrom(sessions.list), [sessions.list]);

    if (sessions.loading) return <SkeletonGrid />;
    if (sessions.error) {
        return (
            <div className="pd-gap">
                <ErrorState kicker="Couldn’t load" title={`${first}’s history didn’t load`} onRetry={sessions.retry}>
                    {sentence(sessions.error)} Game parameters are still available in their tab.
                </ErrorState>
            </div>
        );
    }

    const p = patient.profile || {};
    // The server fills 0 / '' when no assessment exists, so only trust the values when ability_data has entries.
    const hasAbility = Array.isArray(p.ability_data) && p.ability_data.length > 0;
    const now = new Date();
    const thisMonth = sessions.list.filter((s) => {
        const d = new Date(Number(s.startTime));
        return d.getFullYear() === now.getFullYear() && d.getMonth() === now.getMonth();
    }).length;
    const last = sessions.list[0];
    const byGame = {};
    sessions.list.forEach((s) => { const g = gameLabel(s.game); byGame[g] = (byGame[g] || 0) + 1; });
    const top = Object.entries(byGame).sort((a, b) => b[1] - a[1])[0];

    const details = [
        { label: 'Email', value: patient.email || '—' },
        { label: 'Sessions this month', value: String(thisMonth) },
        { label: 'Sessions in total', value: String(sessions.list.length) },
        ...(top ? [{ label: 'Most played', value: `${top[0]} · ${top[1]} session${top[1] === 1 ? '' : 's'}` }] : []),
        { label: 'Last session', value: last ? fmtDateTime(last.startTime, true) : 'None yet' },
        { label: 'Assessment updated', value: hasAbility ? fmtDay(p.timestamp) : 'Not recorded yet' },
    ];

    const columns = [
        {
            key: 'g',
            label: 'Game',
            w: '1.6fr',
            render: (r) => <span className="eg-table__text eg-table__text--strong" title={r.g}><GameCell game={r.g} origin={r.origin} /></span>,
        },
        { key: 'v', label: 'Version', w: '.7fr', muted: true },
        { key: 'st', label: 'Started', w: '1.1fr' },
        { key: 'd', label: 'Duration', w: '.7fr', align: 'right' },
        { key: 's', label: 'Status', w: '1fr', type: 'status' },
    ];
    const visible = showAll ? sessions.list : sessions.list.slice(0, RECENT);
    const rows = visible.map((s) => ({
        id: s._id,
        g: gameLabel(s.game),
        origin: startedByLabel(s, user, nameOf),
        v: s.version || '—',
        st: fmtDateTime(s.startTime),
        d: fmtDuration(sessionLength(s)),
        s: live.sessions.some((l) => l.sessionId === s._id) ? 'live' : STATUSES.has(s.consolidated) ? s.consolidated : null,
        onClick: () => navigate(`/sessions/${s._id}`),
    }));
    const empty = sessions.list.length === 0;

    let reviewBody;
    if (reviews.loading) reviewBody = <Skel h={260} raised />;
    else if (reviews.error) reviewBody = <InlineError title="Reviews didn’t load" message={reviews.error} onRetry={reviews.retry} />;
    else if (!reviews.list.length) reviewBody = <NoteBox>No game reviews yet</NoteBox>;
    else reviewBody = <GameReviewsChart reviews={reviews.list} />;

    return (
        <div className="pd-grid pd-grid--ready">
            <Section id="pd-details" title="Patient details">
                <div className="pd-kv">
                    {details.map((d) => (
                        <div key={d.label} className="pd-kv__row">
                            <span className="pd-kv__k">{d.label}</span>
                            <span className="pd-kv__v">{d.value}</span>
                        </div>
                    ))}
                </div>
            </Section>

            <Section
                id="pd-sessions"
                title="Sessions"
                right={sessions.list.length > RECENT ? (
                    <Button variant="ghost" size="sm" className="pd-viewall" onClick={() => setShowAll((v) => !v)} aria-expanded={showAll}>
                        {showAll ? 'Show recent' : `View all ${sessions.list.length}`}
                    </Button>
                ) : null}
            >
                {empty ? (
                    <NoteBox>No sessions yet. {first}’s first game will appear here as soon as it starts.</NoteBox>
                ) : (
                    <DataTable columns={columns} rows={rows} caption={showAll ? 'All sessions' : 'Recent sessions'} getKey={(r) => r.id} />
                )}
            </Section>

            <Section id="pd-metrics" title="User metrics" note="Latest assessment" card>
                <UserMetricsChart userData={hasAbility ? { ...p, name: patient.name, email: patient.email } : null} />
            </Section>

            <Section id="pd-reviews" title="Game reviews" note="Player ratings by game" card>
                {reviewBody}
            </Section>

            {!empty ? (
                <Section id="pd-rom" title="Range of motion" note="Joint angles per session, degrees" card wide>
                    {joints.length ? (
                        <RangeOfMotionChart data={joints} />
                    ) : (
                        <NoteBox>
                            No joint angles yet
                            {/* which sessions were looked at: how many carry joint angles, and their games */}
                            <Marking
                                name="Joint angles"
                                lines={[
                                    `${new Set(joints.map((j) => j.sessionId)).size} of ${sessions.list.length} sessions`,
                                    Object.entries(byGame).sort((a, b) => b[1] - a[1]).map(([g, n]) => `${g} ${n}`),
                                ]}
                            />
                        </NoteBox>
                    )}
                </Section>
            ) : null}
        </div>
    );
}

/* ── Page ─────────────────────────────────────────────────────────────── */
export default function PatientPage() {
    const { id } = useParams();
    const navigate = useNavigate();
    const [params, setParams] = useSearchParams();
    const linked = useLinkedPlayers();
    const live = useLiveList();

    const tab = params.get('tab') === 'overview' ? 'overview' : 'params';
    const [paramsMounted, setParamsMounted] = useState(tab === 'params');
    useEffect(() => { if (tab === 'params') setParamsMounted(true); }, [tab]);
    const setTab = (t) => {
        const next = new URLSearchParams(params);
        next.set('tab', t);
        setParams(next, { replace: true });
    };

    // A player can be linked twice (data + sponsorship) — prefer the verified link.
    const links = linked.players.filter((pl) => pl.user_id === id);
    const patient = links.find((pl) => pl.verified) || links[0] || null;
    const verified = links.some((pl) => pl.verified);
    const sponsored = links.some((pl) => pl.link_type === 'sponsorship');
    const ready = !!patient && verified;

    const sessions = useSessions(id, ready);
    const reviews = useReviews(id, ready);
    const liveSession = useMemo(() => live.sessions.find((s) => s && String(s.userId) === id) || null, [live.sessions, id]);

    const crumbs = (label) => <div className="pd-crumbs"><Breadcrumb items={[{ label: 'Players', to: '/players' }, { label }]} /></div>;

    /* Linked list still loading (first visit) */
    if (linked.loading && !patient) {
        return (
            <div className="eg-page pd" aria-busy="true">
                {crumbs('Loading…')}
                <div className="pd-head">
                    <Skel w={72} h={72} raised />
                    <div className="pd-id"><Skel w="min(320px, 100%)" h={38} raised /><Skel w="min(420px, 100%)" h={20} raised /></div>
                </div>
                <SkeletonGrid />
            </div>
        );
    }

    if (linked.error && !patient) {
        return (
            <div className="eg-page pd">
                {crumbs('Patient')}
                <div className="pd-gap">
                    <ErrorState title="This patient didn’t load" onRetry={linked.reload}>{linked.error}</ErrorState>
                </div>
            </div>
        );
    }

    if (!patient) {
        return (
            <div className="eg-page pd">
                {crumbs('Not found')}
                <div className="pd-gap">
                    <EmptyState title="Patient not found" action={<Button variant="primary" to="/players">Back to players</Button>} />
                </div>
            </div>
        );
    }

    const name = patient.name;
    const first = String(name).split(/\s+/)[0] || name;
    const initial = String(name).trim().charAt(0).toUpperCase() || '?';
    const lastStart = sessions.list[0]?.startTime;
    // Header figures: time in sessions, in total and per session. Each length is rounded to the second
    // the Sessions table shows, so the total is the sum of the rows as shown. Unknown until loaded.
    const lengths = sessions.loading || sessions.error ? [] : sessions.list.map(sessionLength).filter((ms) => ms > 0).map((ms) => Math.round(ms / 1000) * 1000);
    const played = lengths.reduce((a, b) => a + b, 0);
    const figures = lengths.length ? [
        { value: fmtDuration(played), label: 'Played' },
        { value: fmtDuration(played / lengths.length), label: 'Per session' },
    ] : [];

    let status;
    if (liveSession) {
        status = <DotLabel color="var(--egdl-danger)" ink="var(--egdl-danger)" pulse large>In session · {gameLabel(liveSession.game)}</DotLabel>;
    } else if (!verified) {
        status = <DotLabel color="var(--egdl-warning)" ink="var(--egdl-warning-ink)" large>Link not verified</DotLabel>;
    } else {
        const lastText = !sessions.loading && !sessions.error
            ? (lastStart ? ` · last played ${relativeDay(lastStart)}` : ' · no sessions yet')
            : '';
        status = <DotLabel color="var(--egdl-success)" ink="var(--egdl-success-ink)" large>Active{lastText}</DotLabel>;
    }

    return (
        <div className="eg-page pd">
            {crumbs(name)}

            <header className="pd-head">
                <span className="pd-avatar" aria-hidden="true">
                    {initial}
                    {liveSession ? <span className="pd-avatar__live" /> : null}
                </span>
                <div className="pd-id">
                    <h1 className="pd-name">{name}</h1>
                    <div className="pd-meta">
                        <span className="pd-meta__caps pd-meta__id" title="Player ID">{patient.user_id}</span>
                        <span className="pd-meta__caps">{sponsored ? 'Sponsored player' : 'Shares data with you'}</span>
                        {status}
                    </div>
                </div>
                <Figures className="pd-figures" items={figures} />
                {liveSession ? (
                    <div className="pd-actions">
                        <Button
                            variant="primary"
                            size="sm"
                            icon="live"
                            onClick={() => navigate(`/live/${encodeURIComponent(liveSession.sessionId)}`)}
                            aria-label={`Watch ${name} live in ${gameLabel(liveSession.game)}`}
                        >
                            Watch live
                        </Button>
                    </div>
                ) : null}
            </header>

            <div className="pd-tabs">
                <Tabs tabs={TABS} value={tab} onChange={setTab} label={`${name} sections`} rule large />
            </div>

            {!verified ? (
                <div className="pd-gap">
                    <Banner tone="warning" title="Link not verified.">
                        {first}’s sessions and game parameters appear here once the link between your accounts is verified.
                    </Banner>
                </div>
            ) : (
                <>
                    {tab === 'overview' ? (
                        <div role="tabpanel" aria-label="Overview">
                            <Overview patient={patient} first={first} sessions={sessions} reviews={reviews} />
                        </div>
                    ) : null}
                    {paramsMounted ? (
                        <div role="tabpanel" aria-label="Game parameters" className="pd-params" hidden={tab !== 'params'}>
                            <GameParameters key={id} patientId={id} />
                        </div>
                    ) : null}
                </>
            )}
        </div>
    );
}
